import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ActivatedRouterStateSnapshot, Breadcrumb, PageType, ProductSearchPage, RoutingService} from '@spartacus/core';
import {combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, mergeMap, pluck, switchMap} from 'rxjs/operators';

import {AimoCategory, AimoCategoryHierarchy, AimoFacet} from '../../../../model/product.model';
import {AimoCategoryService} from '../../../../service/category/aimo-category.service';
import {AimoProductFacetService} from '../../../../service/product/aimo-product-facet.service';
import {AimoProductListComponentService} from '../../../../service/product/aimo-product-list-component.service';
import {AimoFacetService} from "../../../../service/product/aimo-facet.service";
import {AimoUxTypeService} from "../../../../service/general/aimo-ux-type.service";

@Component({
    selector: 'aimo-category-header',
    templateUrl: './aimo-category-header.component.html',
    standalone: false
})
export class AimoCategoryHeaderComponent {
    showAllCategories = false;

    model$: Observable<ProductSearchPage>;
    protected routeState$: Observable<ActivatedRouterStateSnapshot>;

    protected category$: Observable<AimoCategory>;

    protected categoryHierarchy$: Observable<AimoCategoryHierarchy>;

    facets$: Observable<AimoFacet>;

    breadCrumbs$: Observable<Breadcrumb[]>;

    total$: Observable<number | string>;

    categoryLevel2Title$: Observable<string>;

    selectedCategoryId$: Observable<string>;

    isMobileApp$: Observable<boolean>;

    constructor(
        protected productFacetService: AimoProductFacetService,
        protected productListComponentService: AimoProductListComponentService,
        protected routing: RoutingService,
        protected categoryService: AimoCategoryService,
        protected activatedRoute: ActivatedRoute,
        protected facetService: AimoFacetService,
        protected aimoUxTypeService: AimoUxTypeService
    ) {
        this.isMobileApp$ = this.aimoUxTypeService.isMobileApp();
        this.model$ = this.productListComponentService.model$;
        this.routeState$ = this.routing.getRouterState().pipe(pluck('state'));
        this.selectedCategoryId$ = this.routeState$.pipe(
            filter((state) => state.context.type === PageType.CATEGORY_PAGE && state.context.id !== null),
            map(state => state.context.id),
            distinctUntilChanged()
        )

        const category$ = combineLatest([this.activatedRoute.queryParams, this.selectedCategoryId$])
            .pipe(
                map(([queryParams, id]) => {
                    const parentCategory: string = queryParams.parentCategory;
                    return parentCategory ?? id;
                }),
                distinctUntilChanged()
            );

        this.category$ = category$.pipe(
            mergeMap((categoryId) => this.categoryService.getCategoryById(categoryId)),
        );

        this.categoryHierarchy$ = category$.pipe(
            mergeMap((categoryId) => this.categoryService.getCategoryHierarchyById(categoryId)),
        );

        this.facets$ = combineLatest([this.category$, this.productFacetService.facetList$]).pipe(
            map(([category, list]) => list.facets.find((facet) => {
                const facetToInspect = category.dummyCategory ? 'categoryLevel2' : 'category';
                return (facet as AimoFacet).code === facetToInspect;
            })),
        );

        this.breadCrumbs$ = combineLatest([this.category$, this.model$]).pipe(
            filter(([category, searchResult]) => searchResult.products.length > 0),
            map(([category, searchResult]) => {
                const crumbs =[...searchResult.breadcrumbs];
                if (!crumbs.find(b => b.facetValueCode === category.code) &&!category.dummyCategory) {
                    crumbs.push({
                        facetValueCode: category.code,
                        facetValueName: category.name,
                    });
                }
                return crumbs;
            }),
        );

        this.total$ = this.model$.pipe(
            switchMap((model) =>
                this.facets$.pipe(
                    map((facet) => {
                        return this.countCategories(facet);
                    }),
                ),
            ),
        );

        this.categoryLevel2Title$ = this.facetService.getCategoryLevel2Title();
    }

    countCategories(category: AimoFacet): number {
        return category?.values?.map((val) => val.count).reduce((p, c) => p + c);
    }

    isAllCategoriesSelected(category: AimoFacet): boolean {
        return category.values.filter((val) => val.selected).length == 0;
    }

    toggleShowAllCategories(showAllCategories: boolean): void {
        this.showAllCategories = showAllCategories;
    }

    isShowSubCategories(categoryLevel: number, category: AimoFacet): boolean {
        return categoryLevel > 1 && category.values.length > 1;
    }

    protected createSelectAllQuery(category: AimoCategory): string {
        return this.activatedRoute.snapshot.queryParams?.query?.replace(/:category:.*/, '') ??
            /*   category.dummyCategory ? '' :*/ ('*:relevance:categoryLevel2:' + category.code);
    }
}
